import { useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material'
import HistoryIcon from '@mui/icons-material/History'
import { ConfirmDialog } from '@components/common/ConfirmDialog'
import { useMobileDialogProps } from '@components/common/useMobileDialogProps'
import { useRedateEarnedAchievements } from '../../hooks/useSystemAchievements'

/**
 * Repairs the completion dates on badges that were awarded before the award path learned to date
 * itself.
 *
 * ## What was wrong
 *
 * Completing a threshold stamped the current time. That is right when the evaluation runs off the
 * activity that earned the badge, and wrong every other time — a recompute or a seed run dates
 * everything it awards to the moment somebody triggered it. Production had "complete your first
 * adventure" reading as September on accounts whose first adventure was in June, sitting in the
 * activity feed three months from its own cause.
 *
 * ## Why it is not just another recalculate
 *
 * Recalculate leaves a finished badge alone, which is exactly what keeps settled dates settled
 * across repeated runs — and therefore what makes it unable to repair one. This is the deliberate
 * exception, which is why it is a separate button with a preview in front of it. It lives behind a
 * toolbar button on the Achievements list so the list itself is the first thing on the tab.
 *
 * Preview first is not ceremony. Dates only ever move backwards, but they move in people's activity
 * feeds and on their profiles, so the count is worth seeing before it happens.
 */
export function AchievementRedateDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const redate = useRedateEarnedAchievements()
  const [applied, setApplied] = useState(false)
  const [confirmApply, setConfirmApply] = useState(false)
  const { fullScreen } = useMobileDialogProps()

  const report = redate.data
  const preview = report && report.dryRun

  return (
    <Dialog open={open} onClose={redate.isPending ? undefined : onClose} maxWidth="sm" fullWidth fullScreen={fullScreen}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <HistoryIcon color="primary" />
        Fix earned dates
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Badges awarded by a recompute carry the date the recompute ran, not the date they were
          earned. This re-dates them from the activity that earned them (the Nth qualifying
          completion, oldest first). Dates only ever move backwards. Preview first to see how many
          would move.
        </Typography>

        <Box sx={{ display: 'flex', gap: 1, mb: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            disabled={redate.isPending}
            onClick={() => {
              setApplied(false)
              redate.mutate(false)
            }}
            startIcon={redate.isPending && !applied ? <CircularProgress size={16} /> : null}
          >
            Preview
          </Button>
          <Button
            variant="contained"
            // Only after a preview, so the number below is what is about to be applied rather than
            // something nobody has looked at.
            disabled={redate.isPending || !preview || report.totalMoved === 0}
            onClick={() => setConfirmApply(true)}
          >
            Apply
          </Button>
        </Box>

        {redate.isError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            Could not run the re-dating pass.
          </Alert>
        )}

        {report && (
          <>
            <Alert severity={report.dryRun ? 'info' : 'success'} sx={{ mb: 1.5 }}>
              {report.dryRun
                ? `${report.totalMoved.toLocaleString()} badge${report.totalMoved === 1 ? '' : 's'} would move. Nothing has been changed yet.`
                : `${report.totalMoved.toLocaleString()} badge${report.totalMoved === 1 ? '' : 's'} re-dated.`}
            </Alert>

            {/* A compact list rather than a five-column table: it fits a phone, and the "left
                alone because" note stays visible. That note is the most useful part: a single
                "482 fixed" hides that every distance badge stayed where it was, and hiding that
                is how somebody later concludes the fix did not work. */}
            <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
              {report.groups
                .filter((g) => g.examined > 0)
                .map((g) => (
                  <Box
                    component="li"
                    key={g.criteriaType}
                    sx={{ py: 1, borderBottom: 1, borderColor: 'divider', '&:last-child': { borderBottom: 0 } }}
                  >
                    <Typography variant="body2" fontWeight="medium" sx={{ overflowWrap: 'anywhere' }}>
                      {g.criteriaType}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" component="div">
                      {g.examined.toLocaleString()} earned · {g.moved.toLocaleString()}{' '}
                      {report.dryRun ? 'would move' : 'moved'}
                      {g.largestCorrectionDays > 0
                        ? ` · largest correction ${Math.round(g.largestCorrectionDays)} days`
                        : ''}
                    </Typography>
                    {g.note && (
                      <Typography variant="caption" color="text.secondary" component="div">
                        Left alone because: {g.note}
                      </Typography>
                    )}
                  </Box>
                ))}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={redate.isPending}>
          Close
        </Button>
      </DialogActions>

      <ConfirmDialog
        open={confirmApply}
        title="Re-date earned badges?"
        message={`${report?.totalMoved.toLocaleString() ?? 0} badge dates will move backwards, in players' activity feeds and on their profiles. This cannot be undone.`}
        confirmLabel="Apply"
        onConfirm={() => {
          setConfirmApply(false)
          setApplied(true)
          redate.mutate(true)
        }}
        onClose={() => setConfirmApply(false)}
      />
    </Dialog>
  )
}
